import React, { useState } from 'react';
import { Card, Steps, Form, Input, Button, Alert, Progress, Space, Tag, List, Typography, Row, Col, Statistic } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;

interface ValidationIssue {
  field: string;
  section: 'product' | 'organisation' | 'brand' | 'category' | 'certification' | 'attributes' | 'observation' | 'productGroup' | 'productModel' | 'person' | 'linking' | 'linkingType' | 'currency' | 'language' | 'thing' | 'offer' | 'mediaObject' | 'property' | 'unit' | 'value';
  message: string;
  severity: 'error' | 'warning' | 'info';
  suggestion: string;
}

interface ValidationResult {
  is_valid: boolean;
  validation_errors: ValidationIssue[];
  missing_fields: string[];
  completeness_score: number;
  compliance_score: number;
  sections: {
    product: number;
    organisation: number;
    brand: number;
    category: number;
    productGroup: number;
    productModel: number;
    person: number;
    certification: number;
    attributes: number;
    observation: number;
    linking: number;
    linkingType: number;
    property: number;
    unit: number;
    value: number;
    currency: number;
    language: number;
    thing: number;
    offer: number;
    mediaObject: number;
  };
}

const DataValidation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});

  // Get data from previous step if available
  const mappedData = location.state?.mappedData;
  const scannedData = location.state?.scannedData;

  React.useEffect(() => {
    if (mappedData) {
      setFormData(mappedData);
      // Auto-validate when data is loaded
      handleValidate();
    }
  }, [mappedData]);

  const steps = [
    {
      title: 'Daten-Überprüfung',
      content: 'data-review',
    },
    {
      title: 'Fehlerbehebung',
      content: 'error-resolution',
    },
    {
      title: 'Final-Validierung',
      content: 'final-validation',
    },
  ];

  const handleValidate = async () => {
    setLoading(true);
    try {
      // DPP schema validation
      const mockResult: ValidationResult = {
        is_valid: false,
        validation_errors: [
          {
            field: 'product.name',
            section: 'product',
            message: 'Produktname (name) ist ein Pflichtfeld gem. DPP',
            severity: 'error',
            suggestion: 'Geben Sie einen Produktnamen ein (max. 512 Zeichen)'
          },
          {
            field: 'product.gtin',
            section: 'product',
            message: 'Keine GTIN angegeben',
            severity: 'warning',
            suggestion: 'GTIN/GTIN-13/GTIN-14 für eindeutige Produktidentifikation hinzufügen'
          },
          {
            field: 'product.eclass',
            section: 'product',
            message: 'eCl@ss Klassifikation fehlt',
            severity: 'warning',
            suggestion: 'eCl@ss Code für standardisierte Klassifikation angeben'
          },
          {
            field: 'organisation.legalName',
            section: 'organisation',
            message: 'Hersteller-Name (legalName) ist ein Pflichtfeld',
            severity: 'error',
            suggestion: 'Geben Sie den offiziellen Firmennamen des Herstellers ein'
          },
          {
            field: 'brand.name',
            section: 'brand',
            message: 'Markenname fehlt',
            severity: 'warning',
            suggestion: 'Markenname für Produktzuordnung angeben'
          },
          {
            field: 'certification.certificationIdentification',
            section: 'certification',
            message: 'Keine Zertifizierungen vorhanden',
            severity: 'info',
            suggestion: 'Zertifizierungen (ISO, CE, etc.) verbessern die Compliance-Bewertung'
          },
          {
            field: 'productGroup.name',
            section: 'productGroup',
            message: 'Produktgruppe nicht zugeordnet',
            severity: 'warning',
            suggestion: 'Produktgruppe für Kategorisierung und Filterung angeben'
          },
          {
            field: 'productModel.name',
            section: 'productModel',
            message: 'Produktmodell fehlt',
            severity: 'warning',
            suggestion: 'Produktmodell-Bezeichnung für Variantenmanagement angeben'
          },
          {
            field: 'person.legalName',
            section: 'person',
            message: 'Kein Ansprechpartner hinterlegt',
            severity: 'info',
            suggestion: 'Ansprechpartner (Person) für Rückfragen zum Produkt angeben'
          },
          {
            field: 'linking.templateName',
            section: 'linking',
            message: 'Keine Verknüpfungen (Linkings) definiert',
            severity: 'info',
            suggestion: 'Verknüpfungen zwischen Produkt, Kategorie und MediaObject anlegen'
          },
          {
            field: 'observation.variableMeasured',
            section: 'observation',
            message: 'Keine Messdaten (Observations) vorhanden',
            severity: 'info',
            suggestion: 'Messdaten und Prüfergebnisse als Observations erfassen'
          },
          {
            field: 'offer.price',
            section: 'offer',
            message: 'Kein Angebot (Offer) mit Preis hinterlegt',
            severity: 'info',
            suggestion: 'Mindestens ein Angebot mit Preis und Währung anlegen'
          },
          {
            field: 'mediaObject.contentUrl',
            section: 'mediaObject',
            message: 'Keine Medien (Bilder, Dokumente) zugeordnet',
            severity: 'warning',
            suggestion: 'Produktbilder oder Datenblätter als MediaObject hinzufügen'
          },
          {
            field: 'thing.name',
            section: 'thing',
            message: 'Keine Things (Bauteile/Komponenten) zugeordnet',
            severity: 'info',
            suggestion: 'Physische Bauteile oder Komponenten als Thing erfassen'
          },
          {
            field: 'currency.currencyAlphaCode3',
            section: 'currency',
            message: 'Keine Währung definiert',
            severity: 'info',
            suggestion: 'Währung (z.B. EUR) für Preisangaben festlegen'
          }
        ],
        missing_fields: [
          'product.name', 'product.gtin', 'product.eclass',
          'organisation.legalName', 'brand.name',
          'productGroup.name', 'productModel.name',
          'person.legalName', 'mediaObject.contentUrl'
        ],
        completeness_score: 35,
        compliance_score: 40,
        sections: {
          product: 40,
          organisation: 30,
          brand: 0,
          category: 50,
          productGroup: 0,
          productModel: 0,
          person: 0,
          certification: 0,
          attributes: 20,
          observation: 0,
          linking: 0,
          linkingType: 0,
          property: 20,
          unit: 20,
          value: 20,
          currency: 0,
          language: 80,
          thing: 0,
          offer: 0,
          mediaObject: 0,
        }
      };

      setValidationResult(mockResult);
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResolveIssue = (field: string) => {
    // Mark issue as resolved
    if (validationResult) {
      const updatedErrors = validationResult.validation_errors.filter(error => error.field !== field);
      const updatedMissingFields = validationResult.missing_fields.filter(f => f !== field);
      
      setValidationResult({
        ...validationResult,
        validation_errors: updatedErrors,
        missing_fields: updatedMissingFields,
        is_valid: updatedErrors.length === 0,
        completeness_score: Math.min(100, validationResult.completeness_score + 5),
        compliance_score: Math.min(100, validationResult.compliance_score + 5)
      });
    }
  };

  const handleFinalValidation = async () => {
    setLoading(true);
    try {
      // Final validation call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update result to valid
      setValidationResult(prev => prev ? {
        ...prev,
        is_valid: true,
        completeness_score: 100,
        compliance_score: 100
      } : null);
      
      setCurrentStep(2);
    } catch (error) {
      console.error('Final validation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDPP = () => {
    // Navigate to DPP creation with validated data
    navigate('/dpp-creation', { state: { validatedData: formData } });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error': return 'red';
      case 'warning': return 'orange';
      case 'info': return 'blue';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error': return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'warning': return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
      case 'info': return <ExclamationCircleOutlined style={{ color: '#1890ff' }} />;
      default: return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {scannedData && (
              <Alert
                message="Gescannte Daten"
                description={`Barcode: ${scannedData.barcode} | Typ: ${scannedData.type} | Quelle: ${scannedData.source}`}
                type="info"
                showIcon
              />
            )}

            {validationResult && (
              <div>
                <Title level={4}>Validierungs-Ergebnisse</Title>
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={12}>
                    <Card size="small">
                      <Progress
                        type="circle"
                        percent={validationResult.completeness_score}
                        format={percent => `${percent}%`}
                        size={80}
                      />
                      <div style={{ textAlign: 'center', marginTop: 8 }}>
                        <Text>Vollständigkeit</Text>
                      </div>
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small">
                      <Progress
                        type="circle"
                        percent={validationResult.compliance_score}
                        format={percent => `${percent}%`}
                        size={80}
                        strokeColor="#52c41a"
                      />
                      <div style={{ textAlign: 'center', marginTop: 8 }}>
                        <Text>Compliance</Text>
                      </div>
                    </Card>
                  </Col>
                </Row>

                {validationResult.sections && (
                  <Card title="DPP Abschnitte" size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={[16, 8]}>
                      {Object.entries(validationResult.sections).map(([key, val]) => (
                        <Col span={4} key={key} style={{ marginBottom: 12 }}>
                          <div style={{ textAlign: 'center' }}>
                            <Progress type="circle" percent={val} size={50} />
                            <div style={{ fontSize: 10, marginTop: 4 }}>{key}</div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card>
                )}

                {validationResult.validation_errors.length > 0 && (
                  <Card title="Validierungs-Fehler (DPP Schema)" size="small">
                    <List
                      dataSource={validationResult.validation_errors}
                      renderItem={(error) => (
                        <List.Item
                          actions={[
                            <Button
                              type="link"
                              size="small"
                              onClick={() => handleResolveIssue(error.field)}
                            >
                              Beheben
                            </Button>
                          ]}
                        >
                          <List.Item.Meta
                            avatar={getSeverityIcon(error.severity)}
                            title={
                              <Space>
                                <Tag color="geekblue">{error.section}</Tag>
                                <span>{error.field}</span>
                                <Tag color={getSeverityColor(error.severity)}>
                                  {error.severity}
                                </Tag>
                              </Space>
                            }
                            description={
                              <div>
                                <div>{error.message}</div>
                                <div style={{ color: '#8c8c8c', fontSize: '12px', marginTop: 4 }}>
                                  Tipp: {error.suggestion}
                                </div>
                              </div>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </Card>
                )}
              </div>
            )}

            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => navigate('/barcode-scanner')}>
                  Zurück zum Scanner
                </Button>
                <Button 
                  type="primary" 
                  onClick={() => setCurrentStep(1)}
                  disabled={!validationResult || validationResult.validation_errors.length === 0}
                >
                  Fehler beheben
                </Button>
                {validationResult?.is_valid && (
                  <Button 
                    type="primary" 
                    onClick={handleCreateDPP}
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  >
                    DPP erstellen
                  </Button>
                )}
              </Space>
            </div>
          </Space>
        );

      case 1:
        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Title level={4}>Fehlende Daten ergänzen</Title>
            
            {validationResult?.missing_fields.map((field) => {
              const sectionLabel: Record<string, string> = {
                'product.name': 'Product.name (string, readOnly)',
                'product.gtin': 'Product.gtin (integer, int64)',
                'product.eclass': 'Product.eclass (string, max 2048)',
                'organisation.legalName': 'Organisation.legalName (string, max 512)',
                'brand.name': 'Brand.name (string, readOnly)',
                'productGroup.name': 'ProductGroup.name (string, readOnly)',
                'productModel.name': 'ProductModel.name (string, readOnly)',
                'person.legalName': 'Person.legalName (string, max 512)',
                'mediaObject.contentUrl': 'MediaObject.contentUrl (string, max 2048)',
              };
              return (
                <Card key={field} size="small" title={sectionLabel[field] || field}>
                  <Form.Item label={`Wert für ${field}`}>
                    <Input
                      placeholder={`Geben Sie ${field} ein`}
                      onChange={(e) => handleFieldChange(field, e.target.value)}
                      onBlur={() => handleResolveIssue(field)}
                    />
                  </Form.Item>
                </Card>
              );
            })}

            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setCurrentStep(0)}>
                  Zurück
                </Button>
                <Button 
                  type="primary" 
                  onClick={handleFinalValidation}
                  loading={loading}
                >
                  Validierung wiederholen
                </Button>
              </Space>
            </div>
          </Space>
        );

      case 2:
        return (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
              <Title level={3} style={{ color: '#52c41a', marginTop: 16 }}>
                Validierung erfolgreich!
              </Title>
              <Text>
                Alle Daten wurden erfolgreich validiert und sind bereit für die DPP-Erstellung.
              </Text>
            </div>

            {validationResult && (
              <Card title="Zusammenfassung">
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Vollständigkeit"
                      value={validationResult.completeness_score}
                      suffix="%"
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Compliance"
                      value={validationResult.compliance_score}
                      suffix="%"
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Status"
                      value="Valid"
                      valueStyle={{ color: '#52c41a' }}
                    />
                  </Col>
                </Row>
              </Card>
            )}

            <div style={{ textAlign: 'center' }}>
              <Space size="large">
                <Button 
                  type="primary" 
                  size="large"
                  icon={<SaveOutlined />}
                  onClick={handleCreateDPP}
                >
                  DPP jetzt erstellen
                </Button>
                <Button 
                  size="large"
                  icon={<ReloadOutlined />}
                  onClick={() => navigate('/barcode-scanner')}
                >
                  Neuen Scan starten
                </Button>
              </Space>
            </div>
          </Space>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px' }}>
      <Card title="Daten-Validierung" bordered={false}>
        <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />
        
        {renderStepContent()}
      </Card>
    </div>
  );
};

export default DataValidation;
